import {
  CheckableAvatarSelectItem,
  DropdownSelect,
} from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg } from 'lib-search-types';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';

type AuthorItem = {
  readonly id: string;
  readonly name: string;
  readonly avatar_url?: string | null;
};

/**
 * The search arg for listing the users that can author audit-log events. No
 * `user_type` filter is applied so all users (not just agents) are searchable.
 */
const initialArg: ReadSearchApiArg<'users'> = {
  scope: 'users',
  sortBy: { field: 'display_name', order: 'asc' },
};

type ActivityLogAuthorFilterProps = {
  /** The currently selected author ids. */
  readonly value: readonly string[];
  /** Called when the selected author ids change. */
  readonly onChange: (authorIds: string[]) => void;
};

/** Represents the component for filtering the activity log by author. */
export const ActivityLogAuthorFilter = ({
  value,
  onChange,
}: ActivityLogAuthorFilterProps): JSX.Element => {
  const [readSearchUsers, { results: users, hasNextPage, fetchNextPage }] =
    useLazyReadSearchInfiniteQuery<'users'>();

  const [searchTerm, setSearchTerm] = useState<string>();

  useUpdateEffect(() => {
    readSearchUsers(
      {
        ...initialArg,
        query: searchTerm,
      },
      { debounceTime: 300 },
    );
  }, [readSearchUsers, searchTerm]);

  const items = useMemo<readonly AuthorItem[]>(
    () =>
      users.map(({ id, name, email, phone_number, avatar_url }) => ({
        id,
        name: name || email || phone_number || id,
        avatar_url,
      })),
    [users],
  );

  const handleChange = useCallback(
    (item: AuthorItem, checked: boolean) => {
      onChange(
        checked ? [...value, item.id] : value.filter((id) => id !== item.id),
      );
    },
    [onChange, value],
  );

  const handleClear = useCallback(() => onChange([]), [onChange]);

  const handleSearch = useCallback((search?: string) => {
    setSearchTerm(search?.trim() || undefined);
  }, []);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !users.length) {
        readSearchUsers({ ...initialArg, query: searchTerm });
      } else if (!isOpen) {
        setSearchTerm(undefined);
      }
    },
    [readSearchUsers, searchTerm, users.length],
  );

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      items={items as AuthorItem[]}
      selectedItems={value as string[]}
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{ variant: 'tertiary', label: value.length || undefined }}
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(fetchNextPage()),
      }}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Authors
    </DropdownSelect>
  );
};
